import { CATEGORIES, DESTINATIONS } from "../../mock";

export const DELIVERY = [["any", "Any time", Infinity], ["3d", "Within 3 days", 3], ["1w", "Within a week", 7], ["3w", "Within 3 weeks", 21], ["1m", "Within a month", 31]];
export const TYPES = [["any", "All formats", () => true], ["evisa", "e-Visa / eTA", (t) => /e-visa|eta/i.test(t)], ["sticker", "Sticker visa", (t) => /sticker/i.test(t)]];
export const DOCS = [["any", "Any documents", () => true], ["passport", "Passport only", (d) => /e-visa|eta/i.test(d.type)], ["full", "Full pack", (d) => /sticker/i.test(d.type)]];
export const LEAD = [["7", "1–7 days", 7], ["1m", "Less than a month", 30], ["2m", "Within 2 months", 60], ["3m", "3 months or more", Infinity]];
export const FLEX = [["Weekend", "This weekend", 3], ["Week", "Within a week", 7], ["Month", "Within a month", 30], ["Any time", "Any time", Infinity]];
const leadDays = (dt) => (dt.mode === "lead" ? LEAD.find((x) => x[0] === dt.lead)?.[2] : dt.mode === "flexible" ? FLEX.find((x) => x[0] === dt.flex)?.[2] : dt.mode === "dates" && dt.from ? Math.max(0, (new Date(dt.from) - Date.now()) / 86400000) : undefined) ?? Infinity;
export const globalMin = () => Math.min(...DESTINATIONS.map((d) => d.fees + Math.min(...Object.values(d.funds || { x: 0 }))));
export const DEFAULT_DATES = { mode: "flexible", lead: "", from: "", to: "", flex: "Any time" };
export const DEFAULT_FILTERS = { where: [], cat: "all", delivery: "any", type: "any", docs: "any", budget: 0, dates: DEFAULT_DATES };

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "");
export function datesLabel(d) {
  if (d.mode === "lead") return d.lead ? `In ${LEAD.find((x) => x[0] === d.lead)?.[1].toLowerCase()}` : "Any lead time";
  if (d.mode === "dates") return d.from ? `${fmt(d.from)}${d.to ? ` – ${fmt(d.to)}` : ""}` : "Select dates";
  return d.flex === "Any time" || !d.flex ? "Any dates" : FLEX.find((x) => x[0] === d.flex)?.[1] || "Flexible";
}
const matchDates = (d, dt) => (d.decisionDays || 30) <= leadDays(dt);

export const SEARCH_CATEGORIES = CATEGORIES.filter((c) => !c.to);
const FUNDS_KEY = { all: "tourist", tourist: "tourist", business: "tourist", evisa: "tourist", student: "student", work: "work", jobs: "work", family: "family", investor: "investor", pr: "pr", citizenship: "pr", transit: "tourist", medical: "tourist", nomad: "work", workingholiday: "tourist", conference: "tourist", religious: "tourist", startup: "investor", research: "student", retirement: "pr" };
export const FUNDS_LABEL = { tourist: "proof of funds for the trip", student: "first-year tuition + living costs", work: "maintenance funds + permit fees", family: "income or sponsorship evidence", investor: "minimum qualifying investment", pr: "settlement fees + funds" };

export const fundsKey = (cat) => FUNDS_KEY[cat] || "tourist";
const govFor = (d, cat) => { const v = d.govFees?.[cat]; return Number.isFinite(v) ? v : Number(d.govFee || 0); };
export const minBudget = (d, cat) => govFor(d, cat) + Number(d.serviceFee || 0) + (d.funds?.[fundsKey(cat)] || 0);
import { money } from "../../lib/locale";
export { money };
export const budgetLabel = (b) => (b ? `Under ${money(b)}` : "Any budget");
export const whereLabel = (where) => {
  if (!where.length) return "Anywhere";
  const names = where.map((s) => DESTINATIONS.find((d) => d.slug === s)?.name).filter(Boolean);
  return names.length > 2 ? `${names[0]} +${names.length - 1}` : names.join(", ");
};
export const catLabel = (cat) => (cat === "all" ? "Any visa" : SEARCH_CATEGORIES.find((c) => c.id === cat)?.label || "Any visa");

export const TRANSIT_HUBS = ["united-arab-emirates", "singapore", "qatar", "turkey", "united-kingdom", "united-states", "canada", "schengen", "germany", "netherlands", "france", "japan", "south-korea", "hong-kong", "malaysia", "thailand"];
export const CAT_ROUTE_RX = { student: /stud|500|f-1|pass/i, work: /work|skilled|employ|482|h-1b|express/i, pr: /express|189|settle|golden|residen|pr\b/i, investor: /golden|invest/i, family: /family|partner|spouse/i, transit: /transit/i };
export const routeFor = (d, cat) => (CAT_ROUTE_RX[cat] ? d.routes.find((r) => CAT_ROUTE_RX[cat].test(r)) : null) || (cat === "transit" ? "Transit visa" : cat === "pr" ? "Residence route" : null);

export function matchCat(d, cat) {
  if (cat === "all") return true;
  if (cat === "evisa") return /e-visa|eta/i.test(d.type);
  if (cat === "transit") return TRANSIT_HUBS.includes(d.slug) || d.routes.some((r) => /transit/i.test(r));
  if (cat === "student") return d.routes.some((r) => /stud|500|f-1|pass/i.test(r));
  if (cat === "work") return d.routes.some((r) => /work|skilled|employ|482|h-1b|express/i.test(r));
  if (cat === "pr") return d.routes.some((r) => /express|189|settle|golden/i.test(r));
  if (cat === "investor") return d.routes.some((r) => /golden|invest/i.test(r));
  if (cat === "family") return d.routes.some((r) => /family|partner/i.test(r));
  return true;
}

export function applyFilters(list, f) {
  const days = DELIVERY.find((x) => x[0] === f.delivery)?.[2] ?? Infinity;
  const type = TYPES.find((x) => x[0] === f.type)?.[2] || TYPES[0][2];
  const docs = DOCS.find((x) => x[0] === f.docs)?.[2] || DOCS[0][2];
  return list.filter((d) => (!f.where.length || f.where.includes(d.slug)) && matchCat(d, f.cat) && d.decisionDays <= days && type(d.type) && docs(d) && (!f.budget || minBudget(d, f.cat) <= f.budget) && matchDates(d, f.dates || DEFAULT_DATES));
}

export function toParams(f) {
  const p = new URLSearchParams();
  if (f.cat !== "all") p.set("cat", f.cat);
  if (f.where.length) p.set("where", f.where.join(","));
  ["delivery", "type", "docs"].forEach((k) => f[k] !== "any" && p.set(k, f[k]));
  if (f.budget) p.set("budget", String(f.budget));
  const dt = f.dates || DEFAULT_DATES;
  if (dt.mode !== DEFAULT_DATES.mode) p.set("when", dt.mode);
  if (dt.mode === "lead" && dt.lead) p.set("lead", dt.lead);
  if (dt.from) p.set("from", dt.from);
  if (dt.to) p.set("to", dt.to);
  if (dt.flex && dt.flex !== DEFAULT_DATES.flex) p.set("flex", dt.flex);
  return p;
}

export function fromParams(p) {
  return {
    where: (p.get("where") || "").split(",").filter(Boolean),
    cat: p.get("cat") || "all",
    delivery: p.get("delivery") || "any",
    type: p.get("type") || "any",
    docs: p.get("docs") || "any",
    budget: Number(p.get("budget")) || 0,
    dates: { mode: p.get("when") || DEFAULT_DATES.mode, lead: p.get("lead") || DEFAULT_DATES.lead, from: p.get("from") || "", to: p.get("to") || "", flex: p.get("flex") || DEFAULT_DATES.flex },
  };
}

export const isDefault = (f) => JSON.stringify(f) === JSON.stringify(DEFAULT_FILTERS);
